import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  GraduationCap,
  Wallet,
  Home as HomeIcon,
  Briefcase,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  MessageCircle,
  ChevronDown,
} from "lucide-react";
import { site } from "@/lib/site";
import { ProfileDiagnostic } from "@/components/resources/ProfileDiagnostic";

type Category = {
  id: string;
  title: string;
  intro: string;
  icon: typeof GraduationCap;
  questions: Array<{
    q: string;
    sample: string;
    logic: string;
  }>;
};

const CATEGORIES: Category[] = [
  {
    id: "academic-intent",
    title: "Academic intent & study plan",
    intro:
      "The officer wants proof you've chosen this program deliberately - not because it was the easiest admit.",
    icon: GraduationCap,
    questions: [
      {
        q: "Why this university?",
        sample:
          "I'm joining the MS in Data Science at NC State because two of its core faculty - Dr. Reich and Dr. Bhamidi - publish in causal inference, which is what I want to work on. Their program also has a 9-month industry practicum at SAS, headquartered 10 minutes from campus, which means I get applied research experience before I graduate.",
        logic:
          "Anchor the answer to 2–3 specific, verifiable details (named faculty, named labs, named practicum). Generic 'good ranking, good campus' answers signal you applied broadly without conviction.",
      },
      {
        q: "Why this course / why MS in [X]?",
        sample:
          "My undergrad capstone was an NLP system for Telugu sentiment analysis. I hit a wall on low-resource transfer learning, which is exactly what the MS focuses on in semesters 2 and 3. I want the depth I can't get in a 6-month online certificate.",
        logic:
          "Tie the program to a specific gap in your current skill set. 'I want to learn more' is not a reason; 'I hit X problem and the program solves it' is.",
      },
      {
        q: "Why study in the US and not in India / Canada / UK?",
        sample:
          "The faculty I want to work with publish in the US, and the assistantship + internship structure lets me do paid research alongside coursework. India's MS programs don't offer that integration yet, and the UK's 1-year format is too compressed for the research depth I need.",
        logic:
          "Compare on substance (research output, program structure, lab access) - never on lifestyle, weather, or 'opportunities.'",
      },
      {
        q: "How many universities did you apply to? Which admits / rejects?",
        sample:
          "I applied to six - admitted to NC State, ASU, and UB; rejected from Georgia Tech and CMU; waitlisted at Purdue. I'm going with NC State because of the SAS practicum and the funding offer.",
        logic:
          "Be honest about rejections. Officers cross-check this; a clean, confident answer with a clear 'why this admit' beats an over-polished one.",
      },
    ],
  },
  {
    id: "financial-ability",
    title: "Financial ability & sponsor",
    intro:
      "The officer needs to see that one year of tuition + living is covered today, not 'we'll figure it out.'",
    icon: Wallet,
    questions: [
      {
        q: "Who is sponsoring you? What do they do?",
        sample:
          "My father - he's the proprietor of Kumar Textiles in Guntur, a wholesale business he's run for 22 years. Our ITRs for the last three years show ~₹38L annual income. He's funding me from business savings and a ₹25L education loan from SBI that's already sanctioned.",
        logic:
          "Name the sponsor, name what they do, name the number. Specifics beat 'my dad has a business.'",
      },
      {
        q: "What is the total cost of your program? How will you fund it?",
        sample:
          "Total cost is ~$72,000 over two years - $48,000 tuition and $24,000 living. Year one is covered by ₹25L from SBI education loan and ₹35L from family savings; year two I expect to offset via on-campus assistantship and CPT.",
        logic:
          "Break the number into year 1 vs year 2 and show the year-1 source is liquid today. 'Assistantship' alone is not a funding answer.",
      },
      {
        q: "What is your sponsor's annual income? Can I see the ITR?",
        sample:
          "₹38L for FY24, ₹34L for FY23, ₹31L for FY22. I have the ITRs, bank statements, and the loan sanction letter in my folder if you'd like to see them.",
        logic:
          "Know the last three years cold, in the same currency as your documents. Offer the folder without being asked.",
      },
      {
        q: "Why did you take an education loan if your family can afford it?",
        sample:
          "It separates my education from my father's working capital - his business needs liquidity for inventory cycles. The loan is also tax-deductible under Section 80E, so it's the cleaner financial structure.",
        logic:
          "Frame a loan as a deliberate financial choice, not a sign of weakness. Officers respect planning.",
      },
    ],
  },
  {
    id: "ties-to-home",
    title: "Ties to home country (214(b) territory)",
    intro:
      "Under section 214(b), you must show non-immigrant intent. The officer is listening for a specific reason you'd come back - not a slogan.",
    icon: HomeIcon,
    questions: [
      {
        q: "Do you plan to return to India after your studies?",
        sample:
          "Yes. The Indian fintech sector - companies like Razorpay and Cred - is hiring exactly the ML-ops profile my MS builds. I'd rather lead a team at a Series C in Bangalore than be employee #4,000 in the Bay Area.",
        logic:
          "Name the sector, name 1–2 companies, name the role you want. Vague 'I love my country' answers fail this question.",
      },
      {
        q: "Why should I believe you'll come back?",
        sample:
          "My family runs a 22-year-old business that I'm being trained to take over with a tech overhaul. My fiancée is finishing her residency in Hyderabad in 2027. And the Indian market for my specialization is growing faster than the US one right now.",
        logic:
          "Stack 2–3 independent ties - family/business, relationship, market opportunity - instead of repeating one. A single tie is easy to dismiss.",
      },
      {
        q: "Do you have any relatives in the US?",
        sample:
          "Yes - my uncle has been in New Jersey on H-1B since 2014. I won't be staying with him; I have on-campus housing confirmed in Raleigh.",
        logic:
          "Disclose relatives proactively. Hiding them is the fastest path to a 214(b). Mentioning housing separately defuses the 'you'll just move in with them' concern.",
      },
      {
        q: "What will you do after graduation?",
        sample:
          "I'll use OPT to work 12 months with a US firm to ship a production ML system end-to-end, then return to India to either join one of the fintechs I mentioned or to scale our family business. I'm not planning to file for H-1B.",
        logic:
          "Mentioning OPT is fine - it's a legal part of F-1. What kills the answer is implying you'll stay indefinitely. Bound the US plan to a clear return.",
      },
    ],
  },
  {
    id: "career-profile",
    title: "Career goals & background fit",
    intro:
      "The officer is checking whether your past, your program, and your future actually form a straight line.",
    icon: Briefcase,
    questions: [
      {
        q: "What are your post-graduation career goals?",
        sample:
          "Short term - 12 months of OPT to ship a fraud-detection system in a US fintech. Long term - return to India and lead the ML team at a payments or lending startup, or build out the analytics layer for our family business.",
        logic:
          "Concrete role + concrete timeline + concrete location. 'Become a data scientist' is not a goal; 'lead the ML team at a Series C in Bangalore' is.",
      },
      {
        q: "What was your undergrad GPA / backlog history?",
        sample:
          "8.1 / 10, no backlogs. I worked on three published projects with my professor in semesters 5–7, which is why my SOP and recommendation letters line up with the MS focus.",
        logic:
          "Don't hide a backlog - own it and pivot to what you learned. Officers see the transcript before they see you.",
      },
      {
        q: "Have you worked before? Why study now instead of working?",
        sample:
          "I worked 22 months at Infosys as a data engineer. I left because the problems I was solving were ETL-heavy; the research I want to do - causal inference on customer behavior - needs the MS coursework I can't get on the job.",
        logic:
          "Frame the gap year(s) as deliberate skill-building. Officers worry about 'studying because you couldn't find a job.'",
      },
    ],
  },
];

const RED_FLAGS = [
  "Memorized, identical answers to every question - officers spot scripts in under 10 seconds.",
  "Vague answers about funding ('my father will manage it') with no numbers.",
  "Naming OPT, H-1B, or green-card as the goal - that's immigrant intent.",
  "Saying you'll 'see how it goes' about returning - sounds like you haven't decided.",
  "Not knowing the names of your professors, your courses, or your university's city.",
];

const FAQS = [
  {
    q: "How long is the average F1 visa interview?",
    a: "Most F-1 interviews at US consulates in India last between 90 seconds and 3 minutes. The officer typically asks 3–5 questions and decides within that window - which is exactly why a memorized script falls apart and a structured mental model works.",
  },
  {
    q: "What are the most common reasons F1 visas get refused under 214(b)?",
    a: "Weak ties to India, fuzzy financial documentation, an unclear study-to-career link, an obvious memorized script, or naming H-1B / green card as the goal. Almost every 214(b) we see traces back to one of these five.",
  },
  {
    q: "Should I memorize answers to F1 visa interview questions?",
    a: "No. Officers are trained to detect memorized answers and treat them as a red flag. Memorize the structure - why this university, why this course, who's funding, what comes after - and let the actual sentences come out fresh in the interview.",
  },
  {
    q: "Can I take notes or documents into the F1 visa interview?",
    a: "You can carry your document folder (I-20, DS-160 confirmation, SEVIS fee receipt, financials, transcripts) but you cannot read from notes during the conversation. The interview is verbal and the officer expects eye contact.",
  },
  {
    q: "What documents should I keep ready for F1 visa interview questions about money?",
    a: "Three years of sponsor ITRs, last six months of bank statements, education loan sanction letter (if any), property or business valuation if you're citing those as assets, and a clean one-page funding summary that adds up to two years of program cost.",
  },
  {
    q: "How does WeLivdIt prepare students for F1 visa interview questions?",
    a: "We don't hand you scripts. We do 4 one-on-one mock interviews with our Specialists who role-play as US visa officers, give you a personalized profile + DS-160 review, and rebuild any weak answers using the same officer-logic framework you see on this page.",
  },
];

export const Route = createFileRoute("/f1-visa-interview-questions")({
  head: () => ({
    meta: [
      { title: "F1 Visa Interview Questions & Answers (2026) | WeLivdIt" },
      {
        name: "description",
        content:
          "The 15 most common US F1 visa interview questions with sample answers and the officer logic behind each one, from 1-on-1 mock Specialists.",
      },
      {
        name: "keywords",
        content:
          "US visa interview questions, F1 visa interview questions, F1 visa sample answers, US student visa interview, 214(b) questions, visa interview prep India",
      },
      { property: "og:title", content: "F1 Visa Interview Questions & Sample Answers (2026)" },
      {
        property: "og:description",
        content:
          "15 most-asked F-1 visa interview questions with sample answers and the officer-logic behind each one - by WeLivdIt Specialists.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://welivdit.com/f1-visa-interview-questions" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/f1-visa-interview-questions" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "F1 Visa Interview Questions & Sample Answers (2026)",
          description:
            "A categorized guide to the most common US F-1 visa interview questions with sample answers and officer-logic explanations.",
          author: { "@type": "Organization", name: "WeLivdIt" },
          publisher: {
            "@type": "Organization",
            name: "WeLivdIt",
            logo: { "@type": "ImageObject", url: "https://welivdit.com/favicon.png" },
          },
          datePublished: "2026-06-21",
          dateModified: "2026-06-21",
          mainEntityOfPage: "https://welivdit.com/f1-visa-interview-questions",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "F1 Visa Interview Questions",
              item: "https://welivdit.com/f1-visa-interview-questions",
            },
          ],
        }),
      },
    ],
  }),
  component: F1QuestionsPage,
});

function F1QuestionsPage() {
  const totalQuestions = useMemo(
    () => CATEGORIES.reduce((sum, c) => sum + c.questions.length, 0),
    [],
  );

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-amber-50/50 via-background to-background">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-foreground">Home</Link></li>
              <li aria-hidden>/</li>
              <li className="text-foreground">F1 Visa Interview Questions</li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-amber-800">
              <Sparkles className="h-3 w-3" /> 2026 edition · India consulates
            </span>
            <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl">
              F1 visa interview questions <span className="text-amber-700">- and how to answer them</span>
            </h1>
            <p className="mt-5 text-lg text-muted-foreground">
              {totalQuestions} of the most-asked US student visa questions, grouped by what the officer
              is really testing. Each question comes with a sample answer <em>and</em> the logic behind
              it - because memorizing scripts is the fastest way to a 214(b).
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/full-support"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-cta px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
              >
                Book a 1-on-1 mock <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`https://wa.me/${site.phoneTel.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
                  "Hi WeLivdIt - I'm prepping for my F1 visa interview and saw your questions guide. Can you help?",
                )}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition"
              >
                <MessageCircle className="h-4 w-4" /> Ask on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TOC */}
      <section className="border-b border-border bg-muted/30" aria-label="Question categories">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:border-amber-300 hover:text-amber-700 transition"
              >
                <c.icon className="h-3.5 w-3.5" /> {c.title}
              </a>
            ))}
            <a
              href="#red-flags"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:border-rose-300 hover:text-rose-700 transition"
            >
              <AlertTriangle className="h-3.5 w-3.5" /> Red flags to avoid
            </a>
            <a
              href="#faq"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:border-sky-300 hover:text-sky-700 transition"
            >
              FAQ
            </a>
          </div>
        </div>
      </section>

      {/* Interactive profile diagnostic */}
      <div className="container mx-auto px-4 pt-12">
        <ProfileDiagnostic className="mx-auto max-w-4xl" />
      </div>

      {/* Categories */}
      <div className="container mx-auto px-4 py-16">
        {CATEGORIES.map((cat) => (
          <section key={cat.id} id={cat.id} className="mb-20 scroll-mt-24">
            <header className="mb-8 flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <cat.icon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
                  {cat.title}
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{cat.intro}</p>
              </div>
            </header>

            <div className="space-y-4">
              {cat.questions.map((item, idx) => (
                <QuestionCard key={idx} index={idx + 1} {...item} />
              ))}
            </div>
          </section>
        ))}

        {/* Red flags */}
        <section id="red-flags" className="mb-20 scroll-mt-24">
          <header className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
                Red flags that trigger a 214(b)
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                Patterns we see in nearly every refusal post-mortem.
              </p>
            </div>
          </header>
          <ul className="space-y-3 rounded-2xl border border-border bg-card p-6">
            {RED_FLAGS.map((flag, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-[11px] font-semibold text-rose-700">
                  {i + 1}
                </span>
                {flag}
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-12 scroll-mt-24">
          <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-3">
            {FAQS.map((f, i) => (
              <FaqRow key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-8 sm:p-10">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <h2 className="font-display text-2xl font-semibold text-foreground">
                Reading the questions isn't the same as answering them out loud.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Book a 1-on-1 mock with a WeLivdIt Specialist. We'll grill you the way the officer will -
                then rebuild every weak answer with you.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Link
                to="/full-support"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-cta px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
              >
                See Full Support <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition"
              >
                Compare plans
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function QuestionCard({
  index,
  q,
  sample,
  logic,
}: {
  index: number;
  q: string;
  sample: string;
  logic: string;
}) {
  // Show only the first sentence (or first ~140 chars) as a teaser.
  const firstStop = sample.search(/[.!?]\s/);
  const teaser =
    firstStop > 0 ? sample.slice(0, firstStop + 1) : sample.slice(0, 140);
  const waUrl = `https://wa.me/${site.phoneTel.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
    `Hi WeLivdIt - I'd like the full sample answer for: "${q}"`,
  )}`;

  return (
    <article className="rounded-2xl border border-border bg-card p-6 transition hover:border-amber-300 hover:shadow-md">
      <h3 className="flex items-start gap-3 font-display text-lg font-semibold text-foreground">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-800">
          {index}
        </span>
        <span>{q}</span>
      </h3>
      <div className="mt-4 space-y-4 pl-10">
        {/* Gated sample answer */}
        <div>
          <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Sample answer
          </div>

          <div className="relative">
            {/* Teaser */}
            <p className="text-sm leading-relaxed text-foreground">
              {teaser}
            </p>

            {/* Faded preview of the remaining answer */}
            <div
              aria-hidden
              className="relative mt-2 max-h-24 overflow-hidden select-none"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)",
                maskImage:
                  "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)",
              }}
            >
              <p className="text-sm leading-relaxed text-foreground/70 blur-[2px]">
                {sample.slice(teaser.length)}
              </p>
            </div>

            {/* Premium lock card */}
            <div className="mt-3 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4 sm:p-5">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                <Sparkles className="h-3.5 w-3.5" /> Premium · Locked
              </div>
              <p className="mt-2 font-display text-base sm:text-lg font-semibold text-foreground">
                Unlock Your Custom DS-160 &amp; Profile Narrative
              </p>
              <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                We don't sell copy-pasted templates or generic scripts. In our 1-on-1 mock sessions, we
                evaluate your background, audit your DS-160, and give live, line-by-line feedback to build
                your authentic answer.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs sm:text-sm text-foreground/80">
                {[
                  "Live 1-on-1 feedback on your university & course pitch",
                  "Real-time stress testing under officer pressure",
                  "Personal profile risk audit (Gaps, funding, DS-160 alignment)",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-cta px-4 py-2 text-xs font-medium text-primary-foreground hover:opacity-90 transition"
                >
                  Book a 1-on-1 Mock <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted transition"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> Ask on WhatsApp
                </a>
              </div>
            </div>

          </div>
        </div>

        {/* Why this works - fully visible */}
        <div className="rounded-xl bg-muted/50 p-4">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Why this works
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">{logic}</p>
        </div>
      </div>
    </article>
  );
}


function FaqRow({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-sm font-medium text-foreground">{q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-border px-5 py-4 text-sm leading-relaxed text-muted-foreground">
          {a}
        </div>
      )}
    </div>
  );
}
